/** Integration snippets. Every integration calls the same universal DBIndex API — no provider-specific backends. */
export const API_BASE = "https://api.dbindex.ai";

export const SNIPPETS: { id: string; label: string; language: string; code: string }[] = [
  {
    id: "rest",
    label: "REST (curl)",
    language: "bash",
    code: `curl ${API_BASE}/v1/query \\
  -H "Authorization: Bearer $DBINDEX_API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -d '{
    "question": "What is the current average retail price of diesel in Sydney?",
    "constraints": { "freshness_seconds": 300, "max_query_price": "0.005000" }
  }'`,
  },
  {
    id: "mcp",
    label: "Generic MCP client",
    language: "json",
    code: `{
  "mcpServers": {
    "dbindex": {
      "url": "${API_BASE}/mcp",
      "headers": { "Authorization": "Bearer \${DBINDEX_API_KEY}" }
    }
  }
}`,
  },
  {
    id: "anthropic",
    label: "Anthropic",
    language: "python",
    code: `import anthropic, os

client = anthropic.Anthropic()
response = client.beta.messages.create(
    model="claude-opus-5-5",
    max_tokens=16000,
    betas=["mcp-client-2025-11-20"],
    messages=[{"role": "user", "content": "What products under $500 are available in Sydney right now?"}],
    mcp_servers=[{
        "type": "url",
        "url": "${API_BASE}/mcp",
        "name": "dbindex",
        "authorization_token": os.environ["DBINDEX_API_KEY"],
    }],
    tools=[{"type": "mcp_toolset", "mcp_server_name": "dbindex"}],
)
for block in response.content:
    if block.type == "text":
        print(block.text)`,
  },
  {
    id: "openai",
    label: "OpenAI",
    language: "python",
    code: `from openai import OpenAI
import os

client = OpenAI()
response = client.responses.create(
    model="gpt-5",
    tools=[{
        "type": "mcp",
        "server_label": "dbindex",
        "server_url": "${API_BASE}/mcp",
        "headers": {"Authorization": f"Bearer {os.environ['DBINDEX_API_KEY']}"},
        "require_approval": "never",
    }],
    input="What products under $500 are available in Sydney right now?",
)
print(response.output_text)`,
  },
  {
    id: "gemini",
    label: "Gemini",
    language: "python",
    code: `# Gemini function calling -> DBIndex REST
import os, requests
from google import genai
from google.genai import types

def dbindex_query(question: str, max_query_price: str = "0.002000") -> dict:
    """Answer a question from live DBIndex data products."""
    r = requests.post("${API_BASE}/v1/query",
        headers={"Authorization": f"Bearer {os.environ['DBINDEX_API_KEY']}"},
        json={"question": question, "constraints": {"max_query_price": max_query_price}}, timeout=10)
    r.raise_for_status()
    return r.json()

client = genai.Client()
resp = client.models.generate_content(
    model="gemini-2.5-pro",
    contents="What products under $500 are available in Sydney right now?",
    config=types.GenerateContentConfig(tools=[dbindex_query]),
)
print(resp.text)`,
  },
  {
    id: "langchain",
    label: "LangChain",
    language: "python",
    code: `import os, requests
from langchain_core.tools import tool

@tool
def dbindex_query(question: str) -> dict:
    """Query live, sanitised data products on the DBIndex network. Returns data + provenance."""
    r = requests.post("${API_BASE}/v1/query",
        headers={"Authorization": f"Bearer {os.environ['DBINDEX_API_KEY']}"},
        json={"question": question, "constraints": {"max_query_price": "0.002000"}}, timeout=10)
    r.raise_for_status()
    return r.json()

# agent = create_react_agent(llm, tools=[dbindex_query])`,
  },
  {
    id: "llamaindex",
    label: "LlamaIndex",
    language: "python",
    code: `import os, requests
from llama_index.core.tools import FunctionTool

def dbindex_query(question: str) -> dict:
    """Query live DBIndex data products; returns rows plus provenance and licence."""
    r = requests.post("${API_BASE}/v1/query",
        headers={"Authorization": f"Bearer {os.environ['DBINDEX_API_KEY']}"},
        json={"question": question}, timeout=10)
    r.raise_for_status()
    return r.json()

tool = FunctionTool.from_defaults(fn=dbindex_query)`,
  },
  {
    id: "vercel",
    label: "Vercel AI SDK",
    language: "typescript",
    code: `import { generateText, tool } from "ai";
import { z } from "zod";

const dbindexQuery = tool({
  description: "Query live, sanitised data products on the DBIndex network",
  inputSchema: z.object({ question: z.string(), maxQueryPrice: z.string().default("0.002000") }),
  execute: async ({ question, maxQueryPrice }) => {
    const r = await fetch("${API_BASE}/v1/query", {
      method: "POST",
      headers: { Authorization: \`Bearer \${process.env.DBINDEX_API_KEY}\`, "Content-Type": "application/json" },
      body: JSON.stringify({ question, constraints: { max_query_price: maxQueryPrice } }),
    });
    if (!r.ok) throw new Error((await r.json()).error?.message);
    return r.json();
  },
});

const { text } = await generateText({ model, tools: { dbindexQuery }, prompt: "Products under $500 in Sydney?" });`,
  },
];
